import { useNavigate } from "react-router-dom";

import { PageDirectoryEntryName } from "../../../api/PageDirectory";
import { useAuth } from "../../../providers/AuthProvider";
import RollingCube, { RollingCubeMotion } from "../RollingCube";
import StatSummary, { StatsSummaryConfig } from "../StatSummary";

export type EmployerStatSummaryDisplayProps = {
  showTitle?: boolean;
  navigateOnClick?: boolean;
  config?: StatsSummaryConfig;
};

const EmployerStatSummary = ({
  title,
  imageUrl,
  value,
  page,
  showTitle = false,
  navigateOnClick = false,
  config,
  valueColor,
  alternate,
  showAlternate = false,
  motion,
}: {
  title: string;
  imageUrl: string;
  value: string;
  page: PageDirectoryEntryName;
  valueColor?: string;
  alternate?: {
    title?: string;
    value: string;
    valueColor?: string;
    page?: PageDirectoryEntryName;
  };
  showAlternate?: boolean;
  motion?: RollingCubeMotion;
} & EmployerStatSummaryDisplayProps) => {
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();
  const activePage = showAlternate && alternate?.page ? alternate.page : page;
  const onClick = navigateOnClick
    ? () => navigate(getScopedPageUrl(activePage))
    : undefined;
  const activeFace =
    showAlternate && alternate
      ? {
          title: alternate.title,
          value: alternate.value,
          valueColor: alternate.valueColor ?? valueColor,
        }
      : { title, value, valueColor };

  if (!alternate || motion?.reduceMotion) {
    return (
      <StatSummary
        title={showTitle ? activeFace.title : undefined}
        imageUrl={imageUrl}
        value={activeFace.value}
        valueColor={activeFace.valueColor}
        onClick={onClick}
        config={config}
      />
    );
  }

  return (
    <RollingCube
      showNext={showAlternate}
      durationMs={motion?.durationMs}
      delayMs={motion?.delayMs}
      onClick={onClick}
      next={
        <StatSummary
          title={showTitle ? alternate.title : undefined}
          imageUrl={imageUrl}
          value={alternate.value}
          valueColor={alternate.valueColor ?? valueColor}
          config={config}
        />
      }
    >
      <StatSummary
        title={showTitle ? title : undefined}
        imageUrl={imageUrl}
        value={value}
        valueColor={valueColor}
        config={config}
      />
    </RollingCube>
  );
};

export default EmployerStatSummary;
